import NextApp, { AppContext } from 'next/app';
import { IncomingMessage } from 'http';

import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';

import { Wrapper } from '@/components/_shared/Wrapper';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { ListingsAppProps } from '@/types';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';

import { buildListingsUserCustom, getStatsigEnvFromStage, useInitStatsig } from '../../common/statsig';
import { DatadogInit } from '../../common/monitoring/datadogrum';

import '@/styles/globals.css';

export default function App({ Component, pageProps, serverUrl, stage, statsigClientKey }: ListingsAppProps) {
  const statsigCustom = pageProps.context ? buildListingsUserCustom(pageProps.context) : undefined;
  useInitStatsig({
    clientKey: statsigClientKey,
    stableId: pageProps.stableId,
    custom: statsigCustom,
    environmentTier: getStatsigEnvFromStage(stage),
  });

  const { locale = 'en-US', messages = {} } = pageProps;

  return (
    <QueryClientProvider>
      <IntlProviderWrapper messages={messages} locale={locale}>
        <Wrapper serverUrl={serverUrl} stage={stage} locale={locale}>
          <DatadogInit stage={stage} />
          <ConsentManagerScripts />
          <Component {...pageProps} stage={stage} />
        </Wrapper>
      </IntlProviderWrapper>
    </QueryClientProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage;
  const appProps = await NextApp.getInitialProps(context);

  // Extract serverUrl using robust getDomainUrl function
  const serverUrl = getDomainUrl(req) || getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  const statsigClientKey = process.env.STATSIG_CLIENT_KEY ?? '';

  return {
    ...appProps,
    serverUrl,
    stage,
    statsigClientKey,
  };
};
